import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import type { Quotation, QuotationStatus } from '@tailoring-crm/shared';
import { PageHeader } from '../components/layout/page-header.js';
import {
  QuotationPartyPickerDialog,
  type QuotationParty,
} from '../components/quotations/quotation-party-picker-dialog.js';
import {
  QuotationFormDialog,
  type QuotationFormMode,
} from '../components/quotations/quotation-form-dialog.js';
import { QuotationRow } from '../components/quotations/quotation-row.js';
import { RejectQuotationDialog } from '../components/quotations/reject-quotation-dialog.js';
import { Button, Input, Label, ListSkeleton, cn } from '@tailoring-crm/ui';
import { useAuth } from '../lib/auth/auth-context.js';
import { useQuotationSearch } from '../lib/quotations/use-quotations.js';

/**
 * Every price the shop has out, on a screen of its own.
 *
 * The document belongs to a customer and is WORKED ON there — that is where the conversation is,
 * and where somebody knows why the price is what it is. What no single customer can answer is
 * the question this screen exists for: what have we quoted, and what is still waiting on an
 * answer? A shop chasing money asks that far more often than it asks about any one person.
 *
 * SO THE DEFAULT VIEW IS DRAFTS AND SENT, not everything. A quotation that has been agreed or
 * declined is finished business; a list that opens with a year of them buries the four documents
 * somebody actually has to do something about today. Every status is one tap away.
 *
 * A tailor never gets here — the nav does not offer it and every route behind it 403s — but the
 * screen refuses on its own too, because a URL can be typed.
 */

/** The tabs, in the order the work flows. `null` is "everything". */
const FILTERS: readonly { key: string; status: readonly QuotationStatus[] | null }[] =
  Object.freeze([
    { key: 'open', status: ['draft', 'sent'] },
    { key: 'draft', status: ['draft'] },
    { key: 'sent', status: ['sent'] },
    { key: 'accepted', status: ['accepted'] },
    { key: 'rejected', status: ['rejected'] },
    { key: 'all', status: null },
  ]);

const PAGE_SIZE = 25;

export function QuotationsPage(): ReactElement {
  const { t } = useTranslation();
  const { role } = useAuth();

  const [filterKey, setFilterKey] = useState<string>('open');
  const [search, setSearch] = useState('');
  /**
   * Grown rather than paged, the same way the pipeline's columns grow. A shop reading down a
   * list of prices is looking for one document; making it remember which page that was on is a
   * worse answer than simply lengthening the list.
   */
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  /** Picking who to quote, then the form itself. Two steps, because a price needs a person. */
  const [picking, setPicking] = useState(false);
  const [form, setForm] = useState<{
    mode: QuotationFormMode;
    party: QuotationParty;
    quotation: Quotation | null;
  } | null>(null);
  const [rejecting, setRejecting] = useState<Quotation | null>(null);

  /**
   * Who an existing document is for, read back off the document itself. `leadName` holds the
   * customer's frozen name — see `quotationSchema`.
   */
  const partyOf = (document: Quotation): QuotationParty => ({
    kind: 'customer',
    id: document.customerId ?? '',
    name: document.leadName,
  });

  const params = useMemo(() => {
    const filter = FILTERS.find((one) => one.key === filterKey) ?? FILTERS[0];
    return {
      ...(search.trim() ? { q: search.trim() } : {}),
      ...(filter?.status ? { status: filter.status } : {}),
      page: 1,
      pageSize,
    };
  }, [filterKey, search, pageSize]);

  // Not asked for at all by somebody the API would refuse — see the hook's `enabled`.
  const quotations = useQuotationSearch(params, { enabled: role !== 'tailor' });

  if (role === 'tailor') {
    return (
      <>
        <PageHeader title={t('quotations.title')} />
        <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          {t('quotations.refused')}
        </p>
      </>
    );
  }

  const items = quotations.data?.items ?? [];
  const total = quotations.data?.total ?? 0;

  return (
    <>
      <PageHeader
        title={t('quotations.title')}
        description={t('quotations.description')}
        action={
          <Button onClick={() => setPicking(true)}>
            <FileText aria-hidden="true" />
            {t('quotations.new')}
          </Button>
        }
      />

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="quotation-search" className="sr-only">
            {t('quotations.searchLabel')}
          </Label>
          <Input
            id="quotation-search"
            type="search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              // A new question deserves a fresh list rather than however far the last one grew.
              setPageSize(PAGE_SIZE);
            }}
            placeholder={t('quotations.searchPlaceholder')}
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {FILTERS.map((filter) => (
            <button
              key={filter.key}
              type="button"
              aria-pressed={filterKey === filter.key}
              onClick={() => {
                setFilterKey(filter.key);
                setPageSize(PAGE_SIZE);
              }}
              className={cn(
                'rounded-full border px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                filterKey === filter.key
                  ? 'border-transparent bg-primary text-primary-foreground'
                  : 'border-border hover:bg-accent',
              )}
            >
              {t(`quotations.filter.${filter.key}`)}
            </button>
          ))}
        </div>

        {quotations.isPending ? <ListSkeleton rows={4} /> : null}

        {!quotations.isPending && items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed p-8 text-center">
            <p className="text-sm text-muted-foreground">
              {search.trim() ? t('quotations.noResults') : t('quotations.emptyList')}
            </p>
            {!search.trim() ? (
              <Button onClick={() => setPicking(true)}>{t('quotations.new')}</Button>
            ) : null}
          </div>
        ) : null}

        {items.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {items.map((quotation) => (
              <QuotationRow
                key={quotation.id}
                quotation={quotation}
                // Whose price it is — the one thing this list needs that the customer's own card
                // does not, because there the name is already at the top of the page.
                showCustomer
                onEdit={(document) =>
                  setForm({
                    mode: 'edit',
                    party: partyOf(document),
                    quotation: document,
                  })
                }
                onRevise={(document) =>
                  setForm({
                    mode: 'revise',
                    party: partyOf(document),
                    quotation: document,
                  })
                }
                onReject={setRejecting}
              />
            ))}
          </ul>
        ) : null}

        {items.length > 0 ? (
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm text-muted-foreground">
              {t('quotations.showing', { count: items.length, total })}
            </span>
            {items.length < total ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageSize((current) => current + PAGE_SIZE)}
              >
                {t('quotations.loadMore')}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Who is this for? Quoting somebody not yet in the customer book is not offered here,
          because it would create a record as a side effect of pricing. The dialog links to the
          customer book instead, which is where a person is first written down. */}
      <QuotationPartyPickerDialog
        open={picking}
        onOpenChange={setPicking}
        onPicked={(party) => {
          setPicking(false);
          setForm({ mode: 'create', party, quotation: null });
        }}
      />

      {form ? (
        <QuotationFormDialog
          open
          onOpenChange={(open) => !open && setForm(null)}
          mode={form.mode}
          party={form.party}
          quotation={form.quotation}
          // The list carries documents from every customer, and whether tax applies is a fact
          // about the SHOP, so it is read from the document being worked on rather than guessed.
          registered={form.quotation?.registered ?? false}
          onSaved={() => setForm(null)}
        />
      ) : null}

      <RejectQuotationDialog
        quotation={rejecting}
        onOpenChange={(open) => !open && setRejecting(null)}
      />
    </>
  );
}

export default QuotationsPage;
